import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Monitor, Cable, Wrench, X, Send, CheckCircle, RotateCcw } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentTitle';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion';

const FAQS = [
  {
    q: 'Is Eastwide Electrical a registered electrical contractor?',
    a: 'Yes. Eastwide Electrical Services is a fully registered electrical contractor in Victoria (REC 29030, ABN 50 322 915 594), fully licensed and insured for residential, commercial, and industrial work.'
  },
  {
    q: 'What areas do you service?',
    a: 'We service Victoria-wide, including Melbourne, Geelong, Ballarat, Bendigo, the Mornington Peninsula, and surrounding regional areas.'
  },
  {
    q: 'Do you offer free quotes?',
    a: 'Yes. We provide free, no-obligation quotes on all electrical work. Call 0409 015 781 or email leigh@eastwideelectrical.com.au to request one.'
  },
  {
    q: 'Do you work on both residential and commercial properties?',
    a: 'Yes. We handle everything from home rewiring and switchboard upgrades to large commercial LED installations and ongoing maintenance contracts.'
  },
  {
    q: "What's involved in an LED digital screen installation?",
    a: 'A full LED screen install covers structural mounting, dedicated power supply, electrical wiring and conduit, screen calibration, and compliance certification. We handle commercial, retail, and outdoor displays of any size, plus ongoing servicing.'
  },
  {
    q: 'When do I need a mains upgrade?',
    a: "You typically need a mains upgrade if you're adding solar, an EV charger, ducted air conditioning, a pool, or running a workshop. Older switchboards still using ceramic fuses also usually warrant an upgrade. We assess your current capacity and upgrade safely to single or three-phase as required."
  },
  {
    q: 'How long does an underground wiring job take?',
    a: 'Most residential underground runs (sheds, granny flats, garden lighting) are completed efficiently, with timing depending on trench length, ground conditions, and site access. Larger commercial jobs are scoped during the free quote.'
  },
  {
    q: "What's included in your commercial maintenance service?",
    a: 'Scheduled preventative maintenance, emergency breakdown response, lighting repairs, exit and emergency light testing (AS 2293 compliance), and full electrical safety inspections. We tailor maintenance plans to your site.'
  },
  {
    q: 'How do I request a quote?',
    a: "Call 0409 015 781, email leigh@eastwideelectrical.com.au, or use the quote form on our home page. We'll respond promptly to discuss your job."
  },
];

const HERO_BG = "https://images.pexels.com/photos/17729221/pexels-photo-17729221.jpeg";

const COLS = 20;
const ROWS = 11;
const TOTAL = COLS * ROWS;

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generateInstallOrder() {
  const order = [];
  for (let col = 0; col < COLS; col++) {
    for (let row = ROWS - 1; row >= 0; row--) {
      order.push(row * COLS + col);
    }
  }
  const batched = [];
  for (let i = 0; i < order.length; i += 3) {
    const batch = order.slice(i, i + 3);
    batched.push(...shuffleArray(batch));
  }
  return batched;
}

function LedBillboard({ className }) {
  const [panels, setPanels] = useState(Array(TOTAL).fill(false));
  const [powered, setPowered] = useState(Array(TOTAL).fill(false));
  const [phase, setPhase] = useState('idle');
  const timeoutsRef = useRef([]);
  const hasRun = useRef(false);

  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];
  };

  const runFullSequence = useCallback(() => {
    clearAllTimeouts();
    setPanels(Array(TOTAL).fill(false));
    setPowered(Array(TOTAL).fill(false));
    setPhase('installing');

    const order = generateInstallOrder();
    const installDelay = 18;
    const installDuration = order.length * installDelay + 400;

    order.forEach((idx, i) => {
      const t = setTimeout(() => {
        setPanels(prev => { const next = [...prev]; next[idx] = true; return next; });
      }, i * installDelay);
      timeoutsRef.current.push(t);
    });

    const t = setTimeout(() => {
      setPhase('powering');
      const powerOrder = [];
      for (let col = 0; col < COLS; col++) {
        const colPanels = [];
        for (let row = 0; row < ROWS; row++) colPanels.push(row * COLS + col);
        powerOrder.push(...shuffleArray(colPanels));
      }
      powerOrder.forEach((idx, i) => {
        const t2 = setTimeout(() => {
          setPowered(prev => { const next = [...prev]; next[idx] = true; return next; });
          if (i === powerOrder.length - 1) {
            const t3 = setTimeout(() => setPhase('done'), 300);
            timeoutsRef.current.push(t3);
          }
        }, i * 15);
        timeoutsRef.current.push(t2);
      });
    }, installDuration);
    timeoutsRef.current.push(t);
  }, []);

  const reset = useCallback(() => {
    clearAllTimeouts();
    setPanels(Array(TOTAL).fill(false));
    setPowered(Array(TOTAL).fill(false));
    setPhase('idle');
    setTimeout(() => runFullSequence(), 100);
  }, [runFullSequence]);

  useEffect(() => {
    if (!hasRun.current) {
      hasRun.current = true;
      const img = new Image();
      img.src = HERO_BG;
      img.onload = () => {
        setTimeout(() => runFullSequence(), 200);
      };
      const fallback = setTimeout(() => runFullSequence(), 2000);
      img.onload = () => {
        clearTimeout(fallback);
        setTimeout(() => runFullSequence(), 200);
      };
    }
  }, [runFullSequence]);

  useEffect(() => {
    return () => clearAllTimeouts();
  }, []);

  return (
    <div className={className}>
      <div className="absolute inset-0 bg-[#0a0a0a]">
        <div
          className="absolute inset-0 grid"
          style={{
            gridTemplateColumns: `repeat(${COLS}, 1fr)`,
            gridTemplateRows: `repeat(${ROWS}, 1fr)`,
            gap: '1px',
            background: '#111',
          }}
        >
          {Array.from({ length: TOTAL }, (_, i) => {
            const row = Math.floor(i / COLS);
            const col = i % COLS;
            const isInstalled = panels[i];
            const isPowered = powered[i];

            return (
              <div key={i} className="relative overflow-hidden">
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    background: isInstalled ? '#444444' : '#0a0a0a',
                    transition: 'background 0.15s ease',
                  }}
                />
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    opacity: isInstalled ? 1 : 0,
                    transform: isInstalled ? 'scale(1)' : 'scale(0.6)',
                    transition: 'all 0.2s ease-out',
                    background: '#484848',
                    boxShadow: isInstalled ? 'inset 0 0 1px rgba(255,255,255,0.04)' : 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    opacity: isPowered ? 1 : 0,
                    transition: 'opacity 0.12s ease-out',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      width: `${COLS * 100}%`,
                      height: `${ROWS * 100}%`,
                      left: `${-col * 100}%`,
                      top: `${-row * 100}%`,
                      backgroundImage: `url(${HERO_BG})`,
                      backgroundSize: '100% 100%',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
        style={{ opacity: phase === 'done' ? 0 : 0.85, background: 'rgba(10,10,10,0.9)' }}
      />
      <div
        className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
        style={{ opacity: phase === 'done' ? 1 : 0, background: 'rgba(0,0,0,0.25)' }}
      />

      <button
        onClick={reset}
        data-testid="hero-replay-button"
        className="absolute bottom-6 right-6 z-20 flex items-center gap-2 bg-black/40 backdrop-blur-sm border border-white/15 text-white/70 text-xs uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-black/60 hover:text-white transition-all duration-300"
      >
        <RotateCcw size={12} />
        Replay
      </button>
    </div>
  );
}

const SERVICES = [
  {
    icon: Monitor,
    title: 'LED Digital Screens',
    desc: 'Professional installation and maintenance of LED digital screens for commercial and retail environments.',
    image: '/services/led-banner.jpg'
  },
  {
    icon: Zap,
    title: 'Mains Upgrades',
    desc: 'Comprehensive electrical mains upgrades to meet modern power demands safely and efficiently.',
    image: '/services/mains-upgrades-banner.jpg'
  },
  {
    icon: Cable,
    title: 'Underground Wiring',
    desc: 'Expert underground cabling solutions for residential and commercial properties across Victoria.',
    image: '/services/underground-wiring-banner.jpg'
  },
  {
    icon: Wrench,
    title: 'Commercial Maintenance',
    desc: 'Ongoing commercial electrical maintenance to keep your business running without interruption.',
    image: '/services/commercial-maintenance-banner.jpg'
  }
];

export default function Home() {
  useDocumentMeta(
    "Eastwide Electrical Services | Electrician Servicing Victoria",
    "Registered electrical contractor servicing Victoria. LED digital screens, mains upgrades, underground wiring, and commercial maintenance. Call 0409 015 781 for a free quote."
  );

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '', description: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Quote Request from ${formData.name}`;
    const body =
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Mobile: ${formData.mobile}\n\n` +
      `${formData.description}`;
    const mailto = `mailto:leigh@eastwideelectrical.com.au?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setSubmitted(true);
    setFormData({ name: '', email: '', mobile: '', description: '' });
  };

  return (
    <main>
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[85vh] flex items-center">
        <LedBillboard className="absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 w-full">
          <div className="max-w-2xl bg-black/40 backdrop-blur-[2px] rounded-xl p-8 md:p-10" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>
            <p className="text-[#7AC943] text-sm uppercase tracking-[0.2em] font-semibold mb-6 fade-up stagger-1">
              Registered Electrical Contractor
            </p>
            <h1 data-testid="hero-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1] mb-6 fade-up stagger-2">
              Electrical Solutions
              <br />
              for <span className="text-[#7AC943]">Modern Media</span>
            </h1>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-lg mb-10 fade-up stagger-3">
              Trusted electrical services for homes and businesses across Victoria. From LED screens to underground wiring, we deliver quality workmanship every time.
            </p>
            <div className="flex flex-wrap gap-4 fade-up stagger-4">
              {!showForm && !submitted && (
                <button
                  onClick={() => setShowForm(true)}
                  data-testid="hero-cta-button"
                  className="inline-flex items-center gap-2 bg-[#7AC943] text-black text-sm font-semibold uppercase tracking-[0.1em] px-8 py-3.5 rounded-sm hover:bg-[#68B038] transition-colors duration-300"
                >
                  Get a Quote
                  <ArrowRight size={16} />
                </button>
              )}
              <Link
                to="/services"
                data-testid="hero-secondary-button"
                className="inline-flex items-center gap-2 border-2 border-white text-white text-sm font-semibold uppercase tracking-[0.1em] px-8 py-3.5 rounded-sm hover:bg-white hover:text-[#3D3D3D] transition-all duration-300"
              >
                Our Services
              </Link>
            </div>

            {/* Quote Form */}
            <div
              data-testid="quote-form-container"
              className={`overflow-hidden transition-all duration-500 ease-out ${
                showForm || submitted ? 'max-h-[600px] opacity-100 mt-10' : 'max-h-0 opacity-0 mt-0'
              }`}
            >
              {submitted ? (
                <div data-testid="quote-success-message" className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg p-8 max-w-2xl">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle size={24} className="text-[#7AC943]" />
                    <h3 className="text-white font-heading text-xl">Email Window Opened</h3>
                  </div>
                  <p className="text-gray-300 text-sm">
                    We've opened your email app with the quote request prefilled. Please review and hit send to complete the request. Prefer to call? <a href="tel:0409015781" className="text-[#7AC943] hover:underline">0409 015 781</a>.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setShowForm(false); }}
                    className="mt-4 text-gray-400 text-sm hover:text-white transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="quote-form"
                  className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg p-6 md:p-8 max-w-2xl"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-white font-heading text-lg">Request a Free Quote</h3>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      data-testid="quote-form-close"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      data-testid="quote-name-input"
                      placeholder="Your Name"
                      required
                      value={formData.name}
                      onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-white/10 border border-white/20 rounded-sm px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-[#7AC943] transition-colors"
                    />
                    <input
                      type="email"
                      data-testid="quote-email-input"
                      placeholder="Email Address"
                      required
                      value={formData.email}
                      onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full bg-white/10 border border-white/20 rounded-sm px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-[#7AC943] transition-colors"
                    />
                  </div>
                  <div className="mb-4">
                    <input
                      type="tel"
                      data-testid="quote-mobile-input"
                      placeholder="Mobile Number"
                      required
                      value={formData.mobile}
                      onChange={e => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                      className="w-full bg-white/10 border border-white/20 rounded-sm px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-[#7AC943] transition-colors"
                    />
                  </div>
                  <div className="mb-6">
                    <textarea
                      data-testid="quote-description-input"
                      placeholder="Describe what you need..."
                      required
                      rows={3}
                      value={formData.description}
                      onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full bg-white/10 border border-white/20 rounded-sm px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-[#7AC943] transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="quote-submit-button"
                    className="inline-flex items-center gap-2 bg-[#7AC943] text-black text-sm font-semibold uppercase tracking-[0.1em] px-8 py-3.5 rounded-sm hover:bg-[#68B038] transition-colors duration-300"
                  >
                    Send Request
                    <Send size={14} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-[#7AC943] text-sm uppercase tracking-[0.2em] font-semibold mb-4">What We Do</p>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-[#3D3D3D] tracking-tight">
              Our Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES.map((service, i) => (
              <Link
                to="/services"
                key={service.title}
                data-testid={`service-preview-${i}`}
                className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <service.icon size={20} className="text-[#7AC943]" />
                    <h3 className="font-heading text-lg text-[#3D3D3D]">{service.title}</h3>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{service.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="view-all-services-button"
              className="inline-flex items-center gap-2 text-[#3D3D3D] text-sm font-semibold uppercase tracking-[0.1em] hover:text-[#7AC943] transition-colors duration-300"
            >
              View All Services
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section data-testid="cta-section" className="bg-[#7AC943] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-black tracking-tight mb-4">
            Need an Electrician?
          </h2>
          <p className="text-black/70 text-base mb-8 max-w-lg mx-auto">
            Whether it's a quick repair or a large-scale project, we're here to help. Contact us for a free quote.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0409015781"
              data-testid="cta-call-button"
              className="inline-flex items-center gap-2 bg-[#3D3D3D] text-white text-sm font-semibold uppercase tracking-[0.1em] px-8 py-3.5 rounded-sm hover:bg-black transition-colors duration-300"
            >
              Call 0409 015 781
            </a>
            <a
              href="mailto:leigh@eastwideelectrical.com.au"
              data-testid="cta-email-button"
              className="inline-flex items-center gap-2 border-2 border-[#3D3D3D] text-[#3D3D3D] text-sm font-semibold uppercase tracking-[0.1em] px-8 py-3.5 rounded-sm hover:bg-[#3D3D3D] hover:text-white transition-all duration-300"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="pt-20 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="w-12 h-1 bg-[#7AC943] mb-6" />
              <h3 className="font-heading text-xl text-[#3D3D3D] mb-3">Registered Contractor</h3>
              <p className="text-gray-500 text-sm leading-relaxed">Fully licensed and insured electrical contractor servicing Victoria.</p>
            </div>
            <div>
              <div className="w-12 h-1 bg-[#7AC943] mb-6" />
              <h3 className="font-heading text-xl text-[#3D3D3D] mb-3">Quality Workmanship</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We pride ourselves on delivering high-quality electrical work that meets all Australian standards.</p>
            </div>
            <div>
              <div className="w-12 h-1 bg-[#7AC943] mb-6" />
              <h3 className="font-heading text-xl text-[#3D3D3D] mb-3">Local Expertise</h3>
              <p className="text-gray-500 text-sm leading-relaxed">Servicing Victoria, we understand the unique electrical needs of our local community.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="bg-gray-50 py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="mb-8 text-center">
            <p className="text-[#7AC943] text-sm uppercase tracking-[0.2em] font-semibold mb-4">FAQ</p>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-[#3D3D3D] tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-gray-200"
              >
                <AccordionTrigger className="py-5 text-base md:text-lg font-heading text-[#3D3D3D] hover:no-underline hover:text-[#7AC943] [&[data-state=open]]:text-[#7AC943]">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-gray-600 text-sm md:text-base leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
